import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef, AfterViewInit, ElementRef } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MapService } from '../../../../shared/services/map.service';
import { saveAs } from 'file-saver';
import * as JSZip from 'jszip';
import { MarkupEditorConfig, MarkupSaveResult } from '../../../../shared/components/markup-editor/markup-editor.types';
import { Markup } from '../../../../shared/models/markup';
import { MarkupService } from '../../../../shared/services/markup.service';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../state/app.state';
import { appDataDetails } from '../../../../shared/core/shared.selectors';
import { photoProofOf, verifiedThresholdM } from '../../../../shared/helpers/photo-proof';

@Component({
    selector: 'app-picture-overlay',
    templateUrl: './picture-overlay.component.html',
    styleUrls: ['./picture-overlay.component.scss'],
    standalone: false
})
export class PictureOverlayComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('swiperContainer', { static: false }) swiperContainer?: ElementRef;

  picturesList: any[] = [];
  isVisible = false;
  isContentReady = false; // Track if Swiper and images are ready
  downloadingAll = false;
  showMarkupEditor = false;
  /** Photo proof (GeoStamp): the details panel beside the picture, and the org threshold. */
  proofPanelOpen = false;
  verifiedThresholdM = verifiedThresholdM(null);
  /** Per picture: the viewer shows the untouched original instead of the stamped copy. */
  showOriginal: { [id: string]: boolean } = {};
  markupEditorConfig: MarkupEditorConfig;
  // The picture currently open in the markup editor. Tracked explicitly because
  // the saved markup's `attachment_id` comes back as the integer DB id, which
  // can't be matched against the picture's string id/uuid.
  private editingPicture: any = null;
  pdfPageState: { [id: string]: { currentPage: number; totalPages: number; loading: boolean } } = {};
  pdfPageImageUrls: { [key: string]: string } = {};
  pdfMarkupPreviews: { [key: string]: string } = {};
  pdfHasMarkups: { [id: string]: boolean } = {};
  private pdfObjectUrls: string[] = [];
  private readonly onDestroy = new Subject<void>();
  private pendingPictures: any[] | null = null;
  private initRetries = 0;
  private readonly MAX_INIT_RETRIES = 10;
  private initCheckInterval: any = null;

  constructor(
    public mapService: MapService,
    private cdr: ChangeDetectorRef,
    private markupService: MarkupService,
    private store: Store<fromRoot.State>,
  ) {}

  ngOnInit() {
    this.mapService.isPictureOverlayAvailable = true;
    this.store.select(appDataDetails).pipe(takeUntil(this.onDestroy)).subscribe((data: any) => {
      this.verifiedThresholdM = verifiedThresholdM(data);
    });

    this.mapService.selectedPicturesData
      .pipe(takeUntil(this.onDestroy))
      .subscribe(pictures => {
        if (pictures && pictures.length > 0) {
          // Reset state
          this.isContentReady = false;
          this.initRetries = 0;
          if (this.initCheckInterval) {
            clearInterval(this.initCheckInterval);
          }

          // Store pictures and show overlay
          this.picturesList = pictures;
          this.isVisible = true;
          this.pendingPictures = pictures;

          // Fetch page counts for any PDFs in the list
          pictures.forEach(p => this.initPdfPages(p));

          // Force change detection to create DOM
          this.cdr.detectChanges();

          // Add body class after DOM update
          document.body.classList.add('picture-overlay-open');

          // Start checking for Swiper initialization
          this.waitForSwiperInitialization();
        }
      });
  }

  /**
   * Continuously check if Swiper element is ready, with retry limit
   */
  private waitForSwiperInitialization(): void {
    // First, ensure swiper-container is defined
    const swiperCustomElement = customElements.get('swiper-container');

    if (!swiperCustomElement) {
      console.error('[PictureOverlay] swiper-container custom element not defined');
      this.isContentReady = true;
      this.cdr.detectChanges();
      return;
    }

    this.initCheckInterval = setInterval(() => {
      this.initRetries++;

      const swiperEl = this.swiperContainer?.nativeElement;
      if (swiperEl) {
        clearInterval(this.initCheckInterval);
        this.initCheckInterval = null;

        // Initialize Swiper now
        this.initializeSwiper();
      } else if (this.initRetries >= this.MAX_INIT_RETRIES) {
        clearInterval(this.initCheckInterval);
        this.initCheckInterval = null;

        // Show content anyway (degraded mode)
        console.warn('[PictureOverlay] Swiper element not found after max retries');
        this.isContentReady = true;
        this.cdr.detectChanges();
      }
    }, 100); // Check every 100ms
  }

  /**
   * Initialize Swiper after it becomes available
   */
  private initializeSwiper(): void {
    const swiperEl = this.swiperContainer?.nativeElement;
    if (!swiperEl) {
      console.error('[PictureOverlay] No swiper element in initializeSwiper!');
      return;
    }

    // Configure swiper parameters before initialization
    const swiperParams = {
      slidesPerView: 1,
      navigation: true,
      pagination: true,
      keyboard: true,
      on: {
        init: () => {
          // Mark content as ready when swiper initializes
          this.isContentReady = true;
          this.pendingPictures = null;
          this.cdr.detectChanges();
        }
      }
    };

    // Assign parameters to swiper element
    Object.assign(swiperEl, swiperParams);

    try {
      // Initialize swiper
      swiperEl.initialize();
    } catch (error) {
      console.error('[PictureOverlay] Error calling initialize():', error);
      // Fallback: show content anyway
      this.isContentReady = true;
      this.cdr.detectChanges();
    }
  }

  ngAfterViewInit() {
    // This only fires on component creation, not useful for *ngIf toggle
    // We handle initialization in waitForSwiperInitialization() instead
  }

  /**
   * Called when image loads successfully
   */
  onImageLoad(event: Event): void {
    // Image loaded
  }

  /**
   * Called when any image fails to load
   */
  onImageError(event: Event, picture: any): void {
    // Try to fallback to thumbnail if original fails
    const img = event.target as HTMLImageElement;
    if (img && picture.thumb_url && img.src !== picture.thumb_url) {
      img.src = picture.thumb_url;
    }
  }

  close() {
    this.proofPanelOpen = false;
    // Clean up initialization interval if still running
    if (this.initCheckInterval) {
      clearInterval(this.initCheckInterval);
      this.initCheckInterval = null;
    }

    // Clean up PDF Object URLs
    this.pdfObjectUrls.forEach(url => URL.revokeObjectURL(url));
    this.pdfObjectUrls = [];
    this.pdfPageState = {};
    this.pdfPageImageUrls = {};
    this.pdfMarkupPreviews = {};
    this.pdfHasMarkups = {};

    // Reset state
    this.isVisible = false;
    this.isContentReady = false;
    this.picturesList = [];
    this.pendingPictures = null;
    this.initRetries = 0;

    // Also tear down the markup editor so reopening a picture starts in
    // fullscreen view mode, not annotate mode (the editor and viewer share
    // this overlay, so closing the overlay must reset the editor too).
    this.showMarkupEditor = false;
    this.markupEditorConfig = null;

    // Show header/navigation when overlay closes
    document.body.classList.remove('picture-overlay-open');
    this.cdr.detectChanges();
  }

  onBackdropClick() {
    this.close();
  }

  downloadPicture(picture: any) {
    const attachment = Object.assign({}, picture, { fileType: 'picture' });
    this.download(attachment);
  }

  // --- Photo proof (GeoStamp) ------------------------------------------------

  hasProof(picture: any): boolean {
    return !!photoProofOf(picture);
  }

  toggleProofPanel() {
    this.proofPanelOpen = !this.proofPanelOpen;
    this.cdr.detectChanges();
  }

  /** Report attachments carry `id`; map-marker photos only `uuid`. */
  proofKey(picture: any): string {
    return picture?.id || picture?.uuid;
  }

  showingOriginal(picture: any): boolean {
    return !!this.showOriginal[this.proofKey(picture)];
  }

  toggleOriginal(picture: any) {
    const key = this.proofKey(picture);
    this.showOriginal[key] = !this.showOriginal[key];
    this.cdr.detectChanges();
  }

  download(attachment: any) {
    if (attachment?.fileType === 'picture') {
      saveAs(
        attachment.original_url || attachment.preview,
        attachment.original_name || 'image',
      );
      return;
    }
  }

  async downloadAll() {
    if (this.picturesList.length === 0) {
      return;
    }

    this.downloadingAll = true;

    try {
      const zip = new JSZip();
      const promises = [];
      const fileNameCounter = {};

      // Process each picture
      this.picturesList.forEach((picture, index) => {
        const url = picture.original_url || picture.thumb_url;
        const defaultName = `image_${index + 1}.jpg`;
        // Use original_name or source_name from API (with .jpg extension if needed)
        let filename = picture.original_name || (picture.source_name ? `${picture.source_name}.jpg` : defaultName);

        // Handle duplicate filenames
        if (fileNameCounter[filename]) {
          const nameParts = filename.split('.');
          const extension = nameParts.pop();
          const baseName = nameParts.join('.');
          filename = `${baseName}_${fileNameCounter[filename]}.${extension}`;
          fileNameCounter[picture.original_name]++;
        } else {
          fileNameCounter[filename] = 1;
        }

        // Fetch image as blob
        const promise = fetch(url)
          .then(response => {
            if (!response.ok) {
              throw new Error(`Failed to fetch ${filename}`);
            }
            return response.blob();
          })
          .then(blob => {
            // Add to zip
            zip.file(filename, blob);
          })
          .catch(error => {
            console.error(`Failed to download ${filename}:`, error);
          });

        promises.push(promise);
      });

      // Wait for all images to be fetched
      await Promise.all(promises);

      // Generate zip file
      const content = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });

      // Download zip file with descriptive name (match carousel naming)
      const timestamp = new Date().toISOString().split('T')[0];
      const fileCount = this.picturesList.length;
      saveAs(content, `media_${fileCount}_files_${timestamp}.zip`);

    } catch (error) {
      console.error('Error downloading all pictures:', error);
    } finally {
      this.downloadingAll = false;
    }
  }

  isImageMime(picture: any): boolean {
    if (picture?.mime) {
      return picture.mime.startsWith('image/');
    }
    // Fallback: if there's an image URL, treat it as an image
    const url = picture?.original_url || picture?.thumb_url || '';
    return /\.(jpe?g|png|gif|bmp|webp|tiff?|heic|svg)(\?|$)/i.test(url) || !!url;
  }

  isPdf(picture: any): boolean {
    return picture?.mime === 'application/pdf';
  }

  getDisplayImageSrc(picture: any): string {
    if (this.isPdf(picture) && this.pdfPageState[picture.id]) {
      // If no markups, just use the original preview (master branch behavior)
      if (!this.pdfHasMarkups[picture.id]) {
        return picture.preview_url || picture.thumb_url || picture.original_url;
      }
      const state = this.pdfPageState[picture.id];
      const key = `${picture.id}_${state.currentPage}`;
      // Priority: markup preview > API-rendered page image > attachment preview as fallback
      if (this.pdfMarkupPreviews[key]) {
        return this.pdfMarkupPreviews[key];
      }
      if (this.pdfPageImageUrls[key]) {
        return this.pdfPageImageUrls[key];
      }
      // Fallback while loading
      return picture.preview_url || picture.thumb_url || picture.original_url;
    }
    // The stamped copy (GeoStamp) is what the viewer shows when one exists, unless asked for the original.
    if (picture.stamped_url && !this.showingOriginal(picture)) {
      return picture.stamped_url;
    }
    return picture.original_url || picture.thumb_url;
  }

  initPdfPages(picture: any) {
    if (!this.isPdf(picture) || this.pdfPageState[picture.id]) return;
    this.pdfPageState[picture.id] = { currentPage: 1, totalPages: 1, loading: true };

    // Fetch page count and all markups in parallel
    Promise.all([
      this.markupService.getPdfPageCount(picture.id).toPromise().catch(() => 1),
      this.markupService.getByAttachment(picture.id).toPromise().catch(() => []),
    ]).then(([count, markups]) => {
      this.pdfPageState[picture.id].totalPages = count;
      this.pdfPageState[picture.id].loading = false;
      const hasMarkups = markups?.length > 0;
      this.pdfHasMarkups[picture.id] = hasMarkups;
      // Store markup preview URLs keyed by attachmentId_pageNumber
      if (hasMarkups) {
        markups.forEach((m: Markup) => {
          if (m.preview_url) {
            this.pdfMarkupPreviews[`${picture.id}_${m.page_number}`] = m.preview_url;
          }
        });
        // Only fetch page 1 from API if there are markups (for consistent rendering)
        this.loadPdfPageImage(picture, 1);
      }
      this.cdr.detectChanges();
    });
  }

  goToPdfPage(picture: any, page: number) {
    const state = this.pdfPageState[picture.id];
    if (!state || page < 1 || page > state.totalPages || page === state.currentPage || state.loading) return;
    state.currentPage = page;
    const key = `${picture.id}_${page}`;
    // If we already have a markup preview or cached image, just update
    if (this.pdfMarkupPreviews[key] || this.pdfPageImageUrls[key]) {
      this.cdr.detectChanges();
      return;
    }
    this.loadPdfPageImage(picture, page);
  }

  private loadPdfPageImage(picture: any, page: number) {
    const state = this.pdfPageState[picture.id];
    if (state) state.loading = true;
    this.cdr.detectChanges();
    this.markupService.getPdfPageImage(picture.id, page).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        this.pdfObjectUrls.push(url);
        this.pdfPageImageUrls[`${picture.id}_${page}`] = url;
        if (state) state.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        if (state) state.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  openMarkupEditor(picture: any) {
    this.editingPicture = picture;
    const isDocument = picture.mime === 'application/pdf';
    // Attachment id field differs by source: daily-report API photos use `id`,
    // map-marker photos use `uuid`. Resolve either so the markup save sends a
    // valid attachment_id (otherwise the API rejects with 422).
    const attachmentId = picture.id || picture.uuid;

    if (isDocument) {
      // Fetch page count and existing markup in parallel
      Promise.all([
        this.markupService.getPdfPageCount(attachmentId).toPromise().catch(() => 1),
        this.markupService.getByAttachment(attachmentId, 1).toPromise().catch(() => []),
      ]).then(([pageCount, markups]) => {
        this.markupEditorConfig = {
          imageUrl: picture.original_url || picture.thumb_url,
          originalUrl: picture.original_url,
          attachmentId,
          existingMarkup: markups?.length ? markups[0] : null,
          pageNumber: 1,
          totalPages: pageCount,
          isDocument: true,
          filename: picture.original_name,
        };
        this.showMarkupEditor = true;
      });
    } else {
      this.markupService.getByAttachment(attachmentId, 1).subscribe({
        next: (markups: Markup[]) => {
          this.markupEditorConfig = {
            imageUrl: picture.original_url || picture.thumb_url,
            originalUrl: picture.original_url,
            attachmentId,
            existingMarkup: markups?.length ? markups[0] : null,
            pageNumber: 1,
            filename: picture.original_name,
          };
          this.showMarkupEditor = true;
        },
        error: () => {
          this.markupEditorConfig = {
            imageUrl: picture.original_url || picture.thumb_url,
            originalUrl: picture.original_url,
            attachmentId,
            existingMarkup: null,
            pageNumber: 1,
            filename: picture.original_name,
          };
          this.showMarkupEditor = true;
        },
      });
    }
  }

  closeMarkupEditor() {
    this.showMarkupEditor = false;
    this.markupEditorConfig = null;
    this.editingPicture = null;
  }

  onMarkupSaved(result: MarkupSaveResult) {
    const { markup, previewDataUrl } = result;
    // Resolve the edited picture by reference — the saved markup's attachment_id
    // is the integer DB id and won't match the picture's string id/uuid.
    const picture = this.editingPicture
      || this.picturesList.find(p => (p.id || p.uuid) === this.markupEditorConfig?.attachmentId);
    if (picture) {
      if (!picture.markups) {
        picture.markups = [];
      }
      const existingIdx = picture.markups.findIndex(m => m.id === markup.id);
      if (existingIdx >= 0) {
        picture.markups[existingIdx] = markup;
      } else {
        picture.markups.push(markup);
      }
      // For PDFs, store preview keyed by page to avoid page order issues
      if (previewDataUrl) {
        const pageNumber = markup.page_number || 1;
        if (this.isPdf(picture)) {
          this.pdfMarkupPreviews[`${picture.id}_${pageNumber}`] = previewDataUrl;
          // Mark that this PDF now has markups (enables page navigation)
          if (!this.pdfHasMarkups[picture.id]) {
            this.pdfHasMarkups[picture.id] = true;
            // Need to load page 1 from API for consistent rendering
            this.loadPdfPageImage(picture, 1);
          }
        } else {
          picture.preview_url = previewDataUrl;
          picture.original_url = previewDataUrl;
          picture.thumb_url = previewDataUrl;
        }
      }
    }
    this.closeMarkupEditor();
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    // Clean up initialization interval
    if (this.initCheckInterval) {
      clearInterval(this.initCheckInterval);
      this.initCheckInterval = null;
    }

    // Clean up PDF Object URLs
    this.pdfObjectUrls.forEach(url => URL.revokeObjectURL(url));
    this.pdfObjectUrls = [];

    // Clean up: ensure header is visible when component is destroyed
    document.body.classList.remove('picture-overlay-open');
    this.mapService.isPictureOverlayAvailable = false;
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
